/**
 * share.ts — privacy-friendly sharing for public tool pages.
 *
 * Progressive enhancement on a single [data-tn-share] trigger:
 *   - If `navigator.share` exists (mostly mobile) → native OS share sheet.
 *   - Otherwise → a small popover of STATIC share-intent anchors (no third-party
 *     JS, no trackers, no SDKs): X/Twitter, Facebook, WhatsApp, Telegram,
 *     LinkedIn, Reddit, Email, plus "Copy link".
 *
 * Discord/Instagram have no public web share-intent, so those route through
 * Copy-link. Everything is built on demand to keep the page's initial JS tiny.
 *
 * The trigger element may carry data-share-url / data-share-title /
 * data-share-text; we fall back to the canonical <link>, document.title and the
 * meta description. Emits `toolnexus:tool-page-share` on share for analytics
 * parity with the existing session-share flow.
 */
import { devLog } from "./dev-utils.js";
import { showFormModal } from "../components/modal.js";
// Locale lookup for the popover's non-brand UI strings. Platform names stay
// English (proper nouns). Falls back to the English source when the shell i18n
// table (window.tnT) is absent, so it can never throw.
function t(key, fallback) {
    const translate = window.tnT;
    return typeof translate === "function" ? translate(key, fallback) : fallback;
}
function canonicalUrl() {
    const link = document.querySelector('link[rel="canonical"]');
    if (link?.href)
        return link.href;
    return window.location.href;
}
function metaDescription() {
    const meta = document.querySelector('meta[name="description"]');
    return meta?.content ?? "";
}
function resolveShareData(trigger) {
    const url = trigger.dataset["shareUrl"] || canonicalUrl();
    const title = trigger.dataset["shareTitle"] || document.title;
    const text = trigger.dataset["shareText"] || metaDescription() || title;
    return { url, title, text };
}
const TARGETS = [
    {
        key: "x",
        label: "X / Twitter",
        href: (d) => `https://twitter.com/intent/tweet?text=${encodeURIComponent(d.title)}&url=${encodeURIComponent(d.url)}`,
    },
    {
        key: "facebook",
        label: "Facebook",
        href: (d) => `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(d.url)}`,
    },
    {
        key: "whatsapp",
        label: "WhatsApp",
        href: (d) => `https://api.whatsapp.com/send?text=${encodeURIComponent(`${d.title} ${d.url}`)}`,
    },
    {
        key: "telegram",
        label: "Telegram",
        href: (d) => `https://t.me/share/url?url=${encodeURIComponent(d.url)}&text=${encodeURIComponent(d.title)}`,
    },
    {
        key: "linkedin",
        label: "LinkedIn",
        href: (d) => `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(d.url)}`,
    },
    {
        key: "reddit",
        label: "Reddit",
        href: (d) => `https://www.reddit.com/submit?url=${encodeURIComponent(d.url)}&title=${encodeURIComponent(d.title)}`,
    },
    {
        key: "email",
        label: "Email",
        href: (d) => `mailto:?subject=${encodeURIComponent(d.title)}&body=${encodeURIComponent(`${d.text}\n\n${d.url}`)}`,
    },
];
function emitShareEvent(method, target, data) {
    try {
        window.dispatchEvent(new CustomEvent("toolnexus:tool-page-share", {
            detail: { method, target, url: data.url, title: data.title },
        }));
    }
    catch {
        /* CustomEvent unsupported — non-fatal */
    }
}
async function copyLink(data, popover) {
    const writer = navigator.clipboard?.writeText;
    if (typeof writer === "function") {
        try {
            await writer.call(navigator.clipboard, data.url);
            flashCopied(popover);
            emitShareEvent("copy", "clipboard", data);
            return;
        }
        catch {
            /* fall through to manual-copy modal */
        }
    }
    await showFormModal({
        title: t("js.share.copyThisLink", "Copy this link"),
        submitText: t("js.share.done", "Done"),
        cancelText: t("js.share.close", "Close"),
        fields: [{ name: "url", label: t("js.share.link", "Link"), type: "text", value: data.url }],
    });
    emitShareEvent("copy", "manual", data);
}
function flashCopied(popover) {
    const btn = popover.querySelector('[data-share-copy]');
    if (btn === null)
        return;
    const original = btn.textContent;
    btn.textContent = t("js.share.copied", "Copied!");
    window.setTimeout(() => {
        btn.textContent = original;
    }, 1600);
}
let openPopover = null;
function closePopover() {
    if (openPopover !== null) {
        openPopover.remove();
        openPopover = null;
        document.removeEventListener("click", onDocumentClick, true);
        document.removeEventListener("keydown", onKeydown, true);
    }
}
function onDocumentClick(ev) {
    if (openPopover !== null && !openPopover.contains(ev.target)) {
        closePopover();
    }
}
function onKeydown(ev) {
    if (ev.key === "Escape")
        closePopover();
}
function buildPopover(data) {
    const pop = document.createElement("div");
    pop.className = "tn-share-popover";
    pop.setAttribute("role", "menu");
    pop.setAttribute("aria-label", t("js.share.ariaLabel", "Share this tool"));
    for (const target of TARGETS) {
        const a = document.createElement("a");
        a.className = "tn-share-popover__item";
        a.setAttribute("role", "menuitem");
        a.href = target.href(data);
        a.textContent = target.key === "email" ? t("js.share.email", "Email") : target.label;
        if (target.key !== "email") {
            a.target = "_blank";
            a.rel = "noopener noreferrer";
        }
        a.addEventListener("click", () => {
            emitShareEvent("intent", target.key, data);
            closePopover();
        });
        pop.appendChild(a);
    }
    const copyBtn = document.createElement("button");
    copyBtn.type = "button";
    copyBtn.className = "tn-share-popover__item tn-share-popover__copy";
    copyBtn.setAttribute("role", "menuitem");
    copyBtn.setAttribute("data-share-copy", "");
    copyBtn.textContent = t("js.share.copyLink", "Copy link");
    copyBtn.addEventListener("click", () => {
        void copyLink(data, pop);
    });
    pop.appendChild(copyBtn);
    return pop;
}
/**
 * Native OS share sheet first; otherwise toggle the static share-intent
 * popover anchored inside `host` (which is made position:relative if static).
 * Exported so other surfaces (e.g. the tool-page ⋮ More menu) can reuse the
 * full share flow without duplicating the target list or popover wiring.
 */
async function shareOrPopover(host, data) {
    // Native sheet first (mobile / supported desktop).
    if (typeof navigator.share === "function") {
        try {
            await navigator.share({ title: data.title, text: data.text, url: data.url });
            emitShareEvent("native", "navigator.share", data);
            return;
        }
        catch {
            // User cancelled or share failed — fall back to the popover.
        }
    }
    if (openPopover !== null) {
        closePopover();
        return;
    }
    const pop = buildPopover(data);
    if (getComputedStyle(host).position === "static") {
        host.style.position = "relative";
    }
    host.appendChild(pop);
    openPopover = pop;
    // Defer listener attach so this same click doesn't immediately close it.
    window.setTimeout(() => {
        document.addEventListener("click", onDocumentClick, true);
        document.addEventListener("keydown", onKeydown, true);
    }, 0);
}
async function handleShareClick(trigger, ev) {
    ev.preventDefault();
    await shareOrPopover(trigger.parentElement ?? document.body, resolveShareData(trigger));
}
function init() {
    devLog("share", "Initializing share buttons");
    const triggers = document.querySelectorAll("[data-tn-share]");
    devLog("share", "Found", triggers.length, "share button(s)");
    triggers.forEach((trigger) => {
        if (trigger.dataset["tnShareBound"] === "1")
            return;
        trigger.dataset["tnShareBound"] = "1";
        trigger.addEventListener("click", (ev) => {
            devLog("share", "Share button clicked");
            void handleShareClick(trigger, ev);
        });
    });
}
if (typeof window !== "undefined") {
    if (document.readyState === "loading") {
        document.addEventListener("DOMContentLoaded", init, { once: true });
    }
    else {
        init();
    }
}
export { shareOrPopover };
//# sourceMappingURL=share.js.map